import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IUser } from "../interfaces/user";
import { DOCS_RESET } from "../actions/docs";
import { useAppDispatch, useAppSelector } from ".";
import { DELETE_DOC_RESET, deleteDocs } from "../actions/delete";
import { IDoc } from "../interfaces/document";
import { isDocOwner, isDocUnsigned, isDocSigning } from "../helpers/docPermissions";

interface UseDeleteDocsOptions {
  onSuccess?: () => void;
  navigateBack?: boolean;
}

export const useDeleteDocs = (
  user: IUser,
  options: UseDeleteDocsOptions = { navigateBack: true },
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const deleteDocState = useAppSelector((state) => state.deleteDoc);
  const docs = useAppSelector((state) => state.docs);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [docsToDelete, setDocsToDelete] = useState<IDoc[]>([]);
  const [errorDelete, setErrorDelete] = useState<string>("");

  const canDeleteDoc = useCallback(
    (doc: IDoc): boolean => {
      if (!isDocOwner(doc, user.id) || isDocSigning(doc)) return false;

      const noCounterpartySignatures = doc.counterparties?.every(
        (el) => el.signatures.length === 0,
      );
      const noCounterparties = !doc.counterparties?.length;
      const allCounterpartiesCancelled =
        !!doc.counterparties?.length && doc.counterparties.every((c) => c.cancelled);

      return (
        (isDocUnsigned(doc) && noCounterpartySignatures) ||
        noCounterparties ||
        allCounterpartiesCancelled ||
        doc.cancelled
      );
    },
    [user.id],
  );

  const getEligibleDocs = useCallback(
    (docs: IDoc | IDoc[]): IDoc[] => {
      const docsArray = Array.isArray(docs) ? docs : [docs];
      return docsArray.filter(canDeleteDoc);
    },
    [canDeleteDoc],
  );



const checkAllDocsDeletable = useCallback(
  (input: IDoc | string[]): boolean => {
    if (!input || (Array.isArray(input) && input.length === 0)) return false;

    let docsArray: IDoc[];

    if (Array.isArray(input)) {
      docsArray = docs.list.filter((d: IDoc) => input.includes(d.id));
      
      if (docsArray.length === 0) return false; 
    } else {
      docsArray = [input];
    }

    return docsArray.every(canDeleteDoc);
  },
  [docs.list, canDeleteDoc],
);

  const initiateDelete = useCallback(
    (input: IDoc | string[]) => {
      let docsArray: IDoc[];

      if (Array.isArray(input)) {
        docsArray = docs.list.filter((d: IDoc) => input.includes(d.id));
      } else {
        docsArray = [input];
      }

      const eligible = docsArray.filter(canDeleteDoc);
      if (eligible.length === 0) return;

      setDocsToDelete(eligible);
      setShowConfirmModal(true);
    },
    [docs.list, canDeleteDoc],
  );

  const confirmDelete = useCallback(() => {
    if (docsToDelete.length === 0) return;

    docsToDelete.forEach((doc) => {
      dispatch(deleteDocs(doc.id));
    });

    setShowConfirmModal(false);
  }, [docsToDelete, dispatch]);

  const cancelDelete = useCallback(() => {
    setShowConfirmModal(false);
    setDocsToDelete([]);
  }, []);

  useEffect(() => {
    if (deleteDocState.state === "success") {
      dispatch({ type: DELETE_DOC_RESET });
      dispatch({ type: DOCS_RESET });
      if (options.onSuccess) {
        options.onSuccess();
      } else {
        dispatch({ type: DOCS_RESET });
      }
      if (options.navigateBack) navigate(-1);
    }

    if (deleteDocState.state === "failure") {
      setErrorDelete(deleteDocState.result?.message || "Помилка при видаленні");

      const timer = setTimeout(() => {
        dispatch({ type: DELETE_DOC_RESET });
        setErrorDelete("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [deleteDocState.state, dispatch, navigate]);

  return {
    showConfirmModal,
    docsToDelete,
    errorDelete,
    deleteState: deleteDocState.state,

    docsToDeleteTitles: docsToDelete.map((d) => d.title),

    canDeleteDoc,
    getEligibleDocs,
    checkAllDocsDeletable,
    initiateDelete,
    confirmDelete,
    cancelDelete,
  };
};
